import type { ComponentType, ReactNode } from "react";
import { cn } from "@/shared/lib/cn";

export interface ClosingCardProps {
  icon: ComponentType<{ className?: string; "aria-hidden"?: boolean }>;
  title: string;
  description: string;
  /** El botón o enlace. Lo pone quien usa la tarjeta, no esta. */
  action: ReactNode;
  /** Letra pequeña bajo la acción: una condición, una alternativa. */
  footnote?: ReactNode;
  /** `ink` para la tarjeta que convierte; `soft` para la que acompaña. */
  tone?: "soft" | "ink";
}

/**
 * Una de las dos tarjetas del cierre de la rejilla.
 *
 * ── Por qué es un componente y no dos bloques ────────────────────────
 * El documento pide DOS llamados al final, uno junto al otro. Escritos a
 * mano serían el mismo armazón duplicado, y el día que uno gane una nota
 * al pie el otro se quedaría desalineado. La forma la pone esta tarjeta;
 * los textos y la acción los pone quien la coloca, que es lo que permite
 * que una lleve un botón vivo y la otra uno apagado.
 *
 * ── Los dos tonos ────────────────────────────────────────────────────
 * `ink` es la tarjeta oscura, la que convierte —la franquicia—; `soft` es
 * la clara. Dos tarjetas oscuras compiten y no se elige ninguna, que es
 * el mismo motivo por el que el cierre de franquicias lleva una sola
 * acción.
 *
 * ── El tono `ink` es color PLANO, y antes no lo era ──────────────────
 * Llevaba `bg-ds-grad-hero-card` —#5C4FD6 → #1F1D2B—, retirado el 26-ago
 * con el resto de degradados decorativos. Y el comentario que había aquí
 * mismo ya cantaba el problema sin darse cuenta: decía "blanco sobre
 * #1F1D2B (~16:1)" cuando media tarjeta era #5C4FD6. Medido contra el
 * extremo violeta, que es el que manda:
 *
 *   blanco (el título)     5.9 : 1   ✓ pero justo
 *   white/90 (el cuerpo)   5.1 : 1   ✓ y más justo todavía
 *
 * Con el fondo plano en `ds-ink` (#1F1D2B) hay una sola cifra por color
 * y el comentario deja de mentir:
 *
 *   blanco                16.6 : 1
 *   white/90              13.6 : 1
 *
 * En `soft`: `ds-ink` y `ds-ink-3` sobre blanco, 16:1 y 12:1.
 * `ds-ink-4` y `ds-warm-gray` no aparecen en ninguno.
 */
export function ClosingCard({
  icon: Icon,
  title,
  description,
  action,
  footnote,
  tone = "soft",
}: ClosingCardProps) {
  const isInk = tone === "ink";

  return (
    <div
      className={cn(
        "flex h-full flex-col rounded-ds-xl p-7 sm:p-8",
        isInk
          ? "bg-ds-ink text-white shadow-ds-card"
          : "border border-ds-border bg-white shadow-ds-xs",
      )}
    >
      <span
        className={cn(
          "inline-flex h-11 w-11 items-center justify-center rounded-2xl",
          isInk ? "bg-white/15 text-white" : "bg-ds-mist text-ds-violet-pop",
        )}
      >
        <Icon className="h-5 w-5" aria-hidden={true} />
      </span>

      <h3
        className={cn(
          "mt-5 text-[19px] font-semibold leading-snug tracking-tight",
          isInk ? "text-white" : "text-ds-ink",
        )}
      >
        {title}
      </h3>

      <p
        className={cn(
          "mt-2 text-[14px] leading-relaxed",
          isInk ? "text-white/90" : "text-ds-ink-3",
        )}
      >
        {description}
      </p>

      {/* `mt-auto` alinea las acciones de las dos tarjetas aunque sus
          textos midan distinto. */}
      <div className="mt-auto pt-7">{action}</div>

      {footnote && (
        <p
          className={cn(
            "mt-3 text-[13px] leading-relaxed",
            isInk ? "text-white/90" : "text-ds-ink-3",
          )}
        >
          {footnote}
        </p>
      )}
    </div>
  );
}
